// @givmo/compliance — the organization's sign-in check before signing or sending (CCM-R1-SIGN-WEB;
// D-SIGN-1; slice 2 §6(g), frame 48, in slice 3's frame 82 words, D-CCM-27 item 4), for a host's
// Settings page: Org Machinery OM-17 seam B, a module-owned panel over the module's own operations,
// under card 11 ("Use AI on your organization's data").
//
// ══ WHO SEES IT ════════════════════════════════════════════════════════════════════════════════
//
// A host declares it on `compliance:send_for_signature`, the string the change requires (D-SIGN-2:
// a Compliance Owner or a Global Admin). That string is released only with signing, so where
// signing is dark nobody holds it and the card is absent (the seam's law: absence, never a
// disabled control). A read that answers 404 (signing dark) renders nothing too.
//
// ══ MAKING IT LESS STRICT TAKES THE CHECK IN FORCE ═════════════════════════════════════════════
//
// Raising the level asks for nothing. Lowering it is refused unless the person meets the level in
// force (contract §7.8), with the typed `step_up_required` (`change_compliance_signing_level`), so
// Save runs through "Confirm it's you" (S3's row). A change made from a stale revision is refused
// and records nothing: the card reads the setting again and shows the served line.
//
// ══ AN ACCOUNT THAT CAN'T MEET THE CHECK HERE (Account Security's AS-PWL-2e) ═══════════════════
//
// Lowering is the card's one act the backend refuses with a cure: a session held to the emailed
// code's limits is told to confirm with its passkey or set one up (`passkey_confirmation_required`,
// `passkey_setup_required`); raising, and recording the level already in force, take no check at
// all. Where nothing on the account can answer here (no way in the sheet takes, and no passkey), the
// gate fails Save with `StepUpPasskeyFailure('none_registered')`, and the card does what the send
// page does, with its shared state (`cannot-meet-check.tsx`): its line above Save (SC1, in
// `compliance-signing-model.ts`) and the host's offer to set up a passkey in Save's place, never
// "please try again". A passkey set up brings Save back, and nothing runs until the person presses
// it.

import { useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { isStepUpCancelled } from '@givmo/step-up';
import { failedForWantOfAPasskey, useCannotMeetCheck } from './cannot-meet-check';
import { useCompliance } from './compliance-context';
import { isModuleUnavailable, serverMessage } from './compliance-decode';
import { signingSettingQueryKey } from './compliance-queries';
import { useComplianceActGate } from './compliance-signing-gate';
import { decodeSigningSetting, type SigningLevel } from './compliance-signing-decode';
import {
  LEVEL_CARD_LINE,
  LEVEL_CARD_TITLE,
  LEVEL_PASSKEY_LINE,
  LEVEL_OPTIONS,
  LEVEL_RULE_LINE,
  SAVE,
  cannotMeetCheckToChangeLine,
} from './compliance-signing-model';

const PREFIX = 'compliance-signing-level';

/** The Settings page's line for a single choice that did not save (`APPROVAL_SAVE_FAILED_LINE`). */
const CHANGE_FAILED_LINE = 'We couldn’t save that. Nothing was changed — please try again.';

export interface ComplianceSigningLevelCardProps {
  /** Whether a choice is unsaved or in flight, for the host page's unsaved-changes guard. */
  onDirtyChange?: (dirty: boolean) => void;
}

export function ComplianceSigningLevelCard({ onDirtyChange }: ComplianceSigningLevelCardProps) {
  const { orgRef, isReady, operations, moduleStanding, organizationName } = useCompliance();
  const headingId = useId();
  const queryClient = useQueryClient();
  const gate = useComplianceActGate();
  // AS-PWL-2e: Save refused where nothing on this account can meet the level in force here — the
  // line above Save, and the host's offer in its place (the header).
  const passkeyCheck = useCannotMeetCheck('level');
  const available = isReady && moduleStanding === 'available' && orgRef !== null;
  const queryKey = signingSettingQueryKey(orgRef);
  const read = useQuery({
    queryKey,
    queryFn: () => operations.getSigningSetting(orgRef ?? ''),
    enabled: available,
  });
  const setting = decodeSigningSetting(read.data);
  const [choice, setChoice] = useState<SigningLevel | undefined>(undefined);
  const [saved, setSaved] = useState(false);
  // The record moved (this card's save, another Owner's): the choice follows it.
  useEffect(() => {
    setChoice(setting?.level);
  }, [setting?.level, setting?.revision]);

  const change = useMutation({
    mutationFn: (next: { level: SigningLevel; from: number }) =>
      gate.run(() =>
        operations.setSigningSetting(orgRef ?? '', {
          level: next.level,
          expected_revision: next.from,
        }),
      ),
    onSuccess: (answer) => {
      if (decodeSigningSetting(answer) === undefined) {
        void queryClient.invalidateQueries({ queryKey });
      } else {
        queryClient.setQueryData(queryKey, answer);
      }
      setSaved(true);
      passkeyCheck.reset();
    },
    onError: (error) => {
      passkeyCheck.caught(error);
      void queryClient.invalidateQueries({ queryKey });
    },
  });
  const dirty = setting !== undefined && choice !== undefined && choice !== setting.level;
  useEffect(() => {
    onDirtyChange?.(dirty || change.isPending);
  }, [dirty, change.isPending, onDirtyChange]);

  if (!available) return null;
  if (read.error != null && isModuleUnavailable(read.error)) return null;

  // A dismissal says nothing, and an account the check can't be met by reads the line instead.
  const failure =
    change.error !== null &&
    change.error !== undefined &&
    !isStepUpCancelled(change.error) &&
    !failedForWantOfAPasskey(change.error)
      ? serverMessage(change.error, CHANGE_FAILED_LINE)
      : undefined;

  return (
    <Card {...testIdProp(PREFIX)}>
      <CardHeader>
        <div className="min-w-0">
          <h3
            id={headingId}
            className="font-sans text-s16 font-w600 tracking-givmo text-text-strong"
          >
            {LEVEL_CARD_TITLE}
          </h3>
          <p className="mt-2 max-w-[640px] text-s13 leading-h15 text-balance text-text-tertiary">
            {LEVEL_CARD_LINE}
          </p>
        </div>
      </CardHeader>
      <CardBody className="flex flex-col gap-16 px-20 py-16">
        {setting === undefined ? (
          read.isPending ? (
            <Skeleton className="h-[140px] w-full" testId={`${PREFIX}-loading`} />
          ) : (
            <ErrorMessage testId={`${PREFIX}-read-error`}>
              {serverMessage(read.error, 'Please try again.')}
            </ErrorMessage>
          )
        ) : (
          <>
            <RadioTileGroup
              value={choice ?? setting.level}
              onValueChange={(next) => {
                setSaved(false);
                // The line was about the change refused; a new choice is a new question.
                passkeyCheck.reset();
                setChoice(next as SigningLevel);
              }}
              aria-labelledby={headingId}
              className="gap-12"
              disabled={change.isPending}
              testId={`${PREFIX}-options`}
            >
              {LEVEL_OPTIONS.map((option) => (
                <RadioTile
                  key={option.level}
                  value={option.level}
                  label={option.label}
                  {...(option.description !== undefined ? { description: option.description } : {})}
                  testId={`${PREFIX}-${option.level}`}
                />
              ))}
            </RadioTileGroup>
            <div className="flex flex-col gap-4">
              <p className="text-s12 leading-h15 text-text-tertiary">{LEVEL_RULE_LINE}</p>
              <p className="text-s12 leading-h15 text-text-tertiary">{LEVEL_PASSKEY_LINE}</p>
            </div>
            {passkeyCheck.shown && (
              <p
                className="text-s13 leading-h15 text-text"
                {...testIdProp(`${PREFIX}-cannot-meet-check`)}
              >
                {cannotMeetCheckToChangeLine(organizationName)}
              </p>
            )}
            {failure !== undefined && (
              <ErrorMessage testId={`${PREFIX}-error`}>{failure}</ErrorMessage>
            )}
            {passkeyCheck.offer !== undefined ? (
              <div {...testIdProp(`${PREFIX}-passkey-offer`)}>{passkeyCheck.offer}</div>
            ) : (
              <Button
                appearance="saas"
                tone="primary"
                size="sm"
                className="self-start"
                disabled={!dirty || passkeyCheck.actShut}
                loading={gate.inFlight}
                onClick={() => {
                  if (choice !== undefined)
                    change.mutate({ level: choice, from: setting.revision });
                }}
                testId={`${PREFIX}-save`}
              >
                {SAVE}
              </Button>
            )}
          </>
        )}
        <p role="status" aria-live="polite" className="sr-only" {...testIdProp(`${PREFIX}-status`)}>
          {change.isPending ? 'Saving…' : saved ? 'Saved.' : ''}
        </p>
      </CardBody>
      {gate.dialog}
    </Card>
  );
}
